import styled from 'styled-components';

import { mixins } from '../../style/mixins';
import { isTabletOrMobile } from '../../utils/devices/device';
import {
  FavoriteButtonWrapper as CardFavoriteButtonWrapper,
  IconButtonsWrapper as CardIconButtonsWrapper,
} from '../Card/Card/Card.styled';

interface HideOverlayProps {
  $isHidden: boolean;
}

export const HideOverlay = styled.div<HideOverlayProps>(
  ({ theme: { color, borderRadius }, $isHidden }) => ({
    transition: 'opacity 0.2s ease-in-out',
    backgroundColor: color.background.reversed.default(0.3),
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: color.content.reversed.default(),
    borderRadius: borderRadius.medium,
    opacity: $isHidden ? 1 : 0,
  }),
);

interface OverlayProps {
  $hasOverlay?: boolean;
  $overlayBackgroundColor?: string;
}

export const Overlay = styled.div<OverlayProps>(
  ({ theme: { borderRadius, color }, $hasOverlay = false, $overlayBackgroundColor }) => ({
    width: '100%',
    height: '100%',
    borderRadius: borderRadius.medium,
    transition: 'opacity 0.3s ease-in-out',
    backgroundColor: $overlayBackgroundColor ?? color.background.reversed.default(0.5),
    opacity: $hasOverlay ? 1 : 0,
  }),
);

interface SlideWrapperProps {
  $backgroundUrl?: string;
  $backgroundColor?: string;
}

export const SlideWrapper = styled.div.attrs<SlideWrapperProps>(
  ({ $backgroundColor, $backgroundUrl }) => {
    return {
      style: {
        backgroundColor: $backgroundColor,
        backgroundImage: $backgroundUrl ? `url("${$backgroundUrl}")` : undefined,
      },
    };
  },
)<SlideWrapperProps>(({ theme: { borderRadius } }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: '50% 50%',
  borderRadius: borderRadius.medium,
}));

interface BorderContainerProps {
  $isSelected: boolean;
}

export const BorderContainer = styled.div<BorderContainerProps>(
  ({ theme: { size, color, borderRadius }, $isSelected }) => ({
    width: '100%',
    height: '100%',
    borderRadius: borderRadius.medium,

    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'transparent',
      opacity: $isSelected ? 1 : 0,
      zIndex: 3,
      borderRadius: borderRadius.medium,
      border: $isSelected
        ? `${size.units(0.25)} solid ${color.border.reversed.default()}`
        : `0 solid ${color.border.reversed.default()}`,
    },
  }),
);

export const FavoriteButtonWrapper = styled(CardFavoriteButtonWrapper)({
  position: 'absolute',
  top: 0,
  zIndex: 3,
});

export const IconButtonsWrapper = styled(CardIconButtonsWrapper)({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  zIndex: 2,
  justifyContent: 'center',
});

interface WrapperProps {
  $isSelected: boolean;
  $isFocused: boolean;
  $isClickable: boolean;
  $isFavorite: boolean;
}

export const Wrapper = styled.div<WrapperProps>(
  ({
    theme: { color, borderRadius, size, shadow },
    $isSelected,
    $isClickable,
    $isFavorite,
  }) => ({
    position: 'relative',
    aspectRatio: '16 / 9',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: borderRadius.medium,
    cursor: $isClickable ? 'pointer' : 'default',
    boxShadow: $isSelected
      ? `0px 0px 0px ${size.units(0.375)} ${color.shadow.accent.default()}`
      : shadow.short,
    zIndex: 1,

    '&:hover': {
      [Overlay]: {
        opacity: isTabletOrMobile || !$isClickable ? 0 : 1,
      },
    },

    [HideOverlay]: {
      position: 'absolute',
      zIndex: 2,
      top: 0,
      left: 0,
    },

    [Overlay]: {
      position: 'absolute',
      zIndex: 1,
      top: 0,
      left: 0,
    },

    [FavoriteButtonWrapper]: {
      opacity: $isFavorite ? 1 : 0,
    },

    ...mixins.devices.desktop({
      '&:hover, &:focus-visible': {
        [IconButtonsWrapper]: {
          opacity: 1,
        },

        [FavoriteButtonWrapper]: {
          opacity: 1,
        },
      },

      '&:has(:focus-visible)': {
        [IconButtonsWrapper]: {
          opacity: 1,
        },

        [FavoriteButtonWrapper]: {
          opacity: 1,
        },
      },
    }),
  }),
);
